<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import CannedAttachmentInput from './CannedAttachmentInput.vue';
import AttachmentsPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    CannedAttachmentInput,
    AttachmentsPreview,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      // anexos já salvos mapeados para o shape do AttachmentsPreview
      attachments: (this.edattachments || []).map(a => ({
        id: a.id,
        isNew: false,
        thumb: a.thumb_url || a.file_url,
        resource: {
          name: a.filename,
          type: a.content_type,
          size: a.byte_size,
        },
      })),
      removedAttachmentIds: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    // texto opcional quando há anexo (resposta só-mídia)
    content: {
      required(value) {
        return Boolean(value) || this.attachments.length > 0;
      },
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    onAttachmentUploaded(attachment) {
      this.attachments.push(attachment);
      this.v$.content.$reset();
    },
    // AttachmentsPreview emite a lista JÁ filtrada, não o item removido:
    // descobrimos o que saiu por diferença e marcamos os salvos para purge
    onRemoveAttachment(remainingAttachments) {
      const remainingIds = new Set(remainingAttachments.map(a => a.id));
      this.attachments
        .filter(a => !a.isNew && !remainingIds.has(a.id))
        .forEach(a => this.removedAttachmentIds.push(a.id));
      this.attachments = remainingAttachments;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.removedAttachmentIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          attachment_blob_ids: this.attachments
            .filter(a => a.isNew)
            .map(a => a.blobId),
          remove_attachment_ids: this.removedAttachmentIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full">
          <label>{{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}</label>
          <p class="mb-2 text-xs text-n-slate-11">
            {{ $t('CANNED_MGMT.ATTACHMENTS.HELP') }}
          </p>
          <CannedAttachmentInput
            :current-count="attachments.length"
            @uploaded="onAttachmentUploaded"
          />
          <AttachmentsPreview
            v-if="attachments.length"
            class="mt-2"
            :attachments="attachments"
            @remove-attachment="onRemoveAttachment"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];

  p {
    @apply text-base;
  }
}
</style>
